/** DTOs mirror the backend JSON exactly (snake_case). Stable identifiers, never translations. */

export const FEEDBACK_CATEGORIES = ['new_feature', 'improvement', 'problem', 'other'] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

/** The seven user-visible statuses. They do not form a fixed sequence. */
export const FEEDBACK_STATUSES = [
  'received',
  'under_review',
  'clarification_needed',
  'planned',
  'in_progress',
  'implemented',
  'not_planned',
] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/** A final decision: leaving it again needs a reason. */
export const TERMINAL_STATUSES: readonly FeedbackStatus[] = ['implemented', 'not_planned'];
/** A public explanation is mandatory when moving INTO one of these. */
export const EXPLANATION_REQUIRED: readonly FeedbackStatus[] = [
  'clarification_needed',
  'not_planned',
];

/** The optional "which part of the application" hint. Never a URL or an identifier. */
export const FEEDBACK_AREAS = [
  'home',
  'boards',
  'databases',
  'calendars',
  'workspaces',
  'settings',
  'other',
] as const;
export type FeedbackArea = (typeof FEEDBACK_AREAS)[number];

export interface FeedbackPerson {
  id: string;
  display_name: string;
}

export interface FeedbackAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  is_image: boolean;
  width: number | null;
  height: number | null;
  created_at: string;
}

export interface FeedbackMessage {
  id: string;
  visibility: 'public' | 'internal';
  from_team: boolean;
  /** Only moderators learn which person wrote a team reply. */
  author: FeedbackPerson | null;
  content: string;
  created_at: string;
  attachments: FeedbackAttachment[];
}

export interface FeedbackStatusEvent {
  id: string;
  from_status: FeedbackStatus | null;
  to_status: FeedbackStatus;
  actor: FeedbackPerson | null;
  by_team: boolean;
  note: string | null;
  created_at: string;
}

export type FeedbackModerationKind =
  'assigned' | 'unassigned' | 'reassigned' | 'attention_cleared' | 'attention_raised' | 'withdrawn';

export interface FeedbackModerationEvent {
  id: string;
  kind: FeedbackModerationKind;
  actor: FeedbackPerson | null;
  from_user: FeedbackPerson | null;
  to_user: FeedbackPerson | null;
  created_at: string;
}

export interface SuggestionItem {
  id: string;
  number: number;
  title: string;
  category: FeedbackCategory;
  status: FeedbackStatus;
  area: FeedbackArea | null;
  preview: string;
  created_at: string;
  last_activity_at: string;
  public_message_count: number;
  unread_count: number;
  withdrawn: boolean;
  own: boolean;
  /** Moderator view only. */
  author: FeedbackPerson | null;
  assignee: FeedbackPerson | null;
  needs_attention: boolean | null;
}

export interface Suggestion extends SuggestionItem {
  description: string;
  attachments: FeedbackAttachment[];
  messages: FeedbackMessage[];
  status_events: FeedbackStatusEvent[];
  moderation_events: FeedbackModerationEvent[] | null;
  can_moderate: boolean;
  can_reply: boolean;
}

export interface SuggestionPage {
  items: SuggestionItem[];
  next_cursor: string | null;
}

export type FeedbackScope = 'mine' | 'all';
export type FeedbackSort = 'activity_desc' | 'activity_asc' | 'created_desc' | 'created_asc';

export interface FeedbackFilter {
  scope: FeedbackScope;
  q: string;
  status: FeedbackStatus | '';
  category: FeedbackCategory | '';
  sort: FeedbackSort;
  /** Moderators: '', 'me', 'unassigned' or a moderator's id. */
  assigned: string;
  attention: boolean;
  unread: boolean;
}

export interface NewSuggestionInput {
  title: string;
  category: FeedbackCategory;
  description: string;
  area: FeedbackArea | null;
  attachment_ids: string[];
  client_key: string;
}
